import { useCallback, useEffect, useState } from 'react';

/**
 * Tool-state persistence helper.
 *
 * Each interactive tool (BudgetCalculator, VisaChecklist, etc.) keeps its
 * state under a deterministic key so users get their progress back across
 * sessions. Reset clears the entry and reverts to the initial value.
 *
 * @param {string} toolName  Short slug (e.g. "budget", "visa", "converter").
 * @param {*}      initial   Initial state when nothing is stored.
 * @returns {[any, (next: any | ((prev:any) => any)) => void, () => void]}
 *
 * @example
 *   const [state, setState, reset] = useToolState('budget', { housing: 0 });
 */
export function useToolState(toolName, initial) {
  const key = `tool_${toolName}_state`;

  const read = useCallback(() => {
    if (typeof window === 'undefined') return initial;
    try {
      const raw = window.localStorage.getItem(key);
      return raw === null ? initial : JSON.parse(raw);
    } catch {
      return initial;
    }
  }, [key, initial]);

  const [value, setValue] = useState(read);

  const write = useCallback(
    (next) => {
      setValue((prev) => {
        const resolved = typeof next === 'function' ? next(prev) : next;
        try {
          window.localStorage.setItem(key, JSON.stringify(resolved));
        } catch {
          /* private mode / quota */
        }
        return resolved;
      });
    },
    [key],
  );

  const reset = useCallback(() => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
    setValue(initial);
  }, [key, initial]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== key) return;
      try {
        setValue(e.newValue === null ? initial : JSON.parse(e.newValue));
      } catch {
        setValue(initial);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [key, initial]);

  return [value, write, reset];
}
